import { BRAND_LOGO } from '../../brand'

/**
 * «تذكّرني على هذا الجهاز».
 *
 * البريد يُحفظ بتخزين المتصفح ليُعبَّأ تلقائياً. أما كلمة المرور فتُسلَّم لمدير كلمات
 * المرور في المتصفح نفسه (Credential Management API) فيحفظها مشفّرةً ويملؤها لاحقاً
 * — ولا تُكتب في localStorage أبداً: أي سكربت يعمل بالصفحة يستطيع قراءته.
 * المتصفحات التي لا تدعم الواجهة (سفاري/فايرفوكس) تلتقط النموذج بنفسها من سمات
 * autocomplete على الحقلين.
 */
const FLAG_KEY = 'ufuq_login_remember'
const EMAIL_KEY = 'ufuq_login_email'

type PasswordCredentialInit = { id: string; password: string; name?: string; iconURL?: string }
type PasswordCredentialCtor = new (data: PasswordCredentialInit) => Credential
type PasswordCredentialLike = Credential & { password?: string }

const passwordCredentialCtor = (): PasswordCredentialCtor | undefined =>
  (window as unknown as { PasswordCredential?: PasswordCredentialCtor }).PasswordCredential

export function readRememberedLogin(): { remember: boolean; email: string } {
  try {
    const remember = localStorage.getItem(FLAG_KEY) === '1'
    return { remember, email: remember ? (localStorage.getItem(EMAIL_KEY) ?? '') : '' }
  } catch {
    // تخزين المتصفح محجوب (نافذة خاصة/إعدادات) — الشاشة تعمل بلا تذكّر
    return { remember: false, email: '' }
  }
}

/** بعد دخولٍ ناجح: يحفظ الاختيار، ويسلّم بيانات الدخول لمدير كلمات المرور بالمتصفح */
export function rememberLogin(
  remember: boolean,
  email: string,
  password: string,
  displayName?: string,
) {
  try {
    if (remember) {
      localStorage.setItem(FLAG_KEY, '1')
      localStorage.setItem(EMAIL_KEY, email)
    } else {
      localStorage.setItem(FLAG_KEY, '0')
      localStorage.removeItem(EMAIL_KEY)
    }
  } catch {
    /* تخزين المتصفح محجوب — لا شيء يُحفظ */
  }

  const Ctor = passwordCredentialCtor()
  if (!remember || !Ctor || !navigator.credentials?.store) return
  try {
    const cred = new Ctor({
      id: email,
      password,
      name: displayName || email,
      iconURL: new URL(BRAND_LOGO, window.location.origin).href,
    })
    // يعرض المتصفح «حفظ كلمة المرور؟» أو يحدّثها — لا ننتظره قبل دخول النظام
    void navigator.credentials.store(cred).catch(() => {})
  } catch {
    /* متصفح لا يدعم الواجهة — مدير كلمات المرور يلتقط النموذج بنفسه */
  }
}

/** البيانات المحفوظة لهذا الموقع من مدير كلمات المرور (يعرض اختيار الحساب إن لزم) */
export async function loadBrowserCredential(): Promise<{ email: string; password: string } | null> {
  if (!passwordCredentialCtor() || !navigator.credentials?.get) return null
  try {
    const c = (await navigator.credentials.get({
      password: true,
      mediation: 'optional',
    } as CredentialRequestOptions)) as PasswordCredentialLike | null
    if (!c || c.type !== 'password' || !c.password) return null
    return { email: c.id, password: c.password }
  } catch {
    return null
  }
}

/** عند الخروج: لا دخول صامت بعد خروجٍ مقصود — الاختيار يعود للمستخدم */
export function preventSilentSignIn() {
  try {
    void navigator.credentials?.preventSilentAccess?.().catch(() => {})
  } catch {
    /* غير مدعوم */
  }
}
